import React, { useState, useEffect } from 'react';
import NotificationDetailModal from './NotificationDetailModal.tsx';
import { linkify } from '../../utils/linkify.tsx';
import { AppIcon, AppIconName, notificationTypeIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'general' | 'payment' | 'event' | 'urgent' | 'maintenance' | 'grounds' | 'horse';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdAt: string;
  isRead: boolean;
  expiresAt: string | null;
  creator: {
    firstName: string;
    lastName: string;
  };
}

interface NotificationsListProps {
  compact?: boolean;
  maxItems?: number;
  onViewAll?: () => void;
}

export default function NotificationsList({ compact = false, maxItems, onViewAll }: NotificationsListProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    grounds: false,
    horse: false,
    general: false
  });
  const [dismissing, setDismissing] = useState<string | null>(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429 && retryCount < 3) {
        // Retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Rate limited, retrying in ${delay}ms...`);
        setTimeout(() => fetchNotifications(retryCount + 1), delay);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse notifications response:', parseError);
        return;
      }

      if (data.success) {
        // Filter out expired notifications
        const activeNotifications = data.data.filter((notification: Notification) => {
          if (!notification.expiresAt) return true;
          return new Date(notification.expiresAt) > new Date();
        });
        setNotifications(activeNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      if (retryCount === 0) { // Only set loading false on first attempt
        setLoading(false);
      }
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Update the local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      );

      // Update selected notification if it's the same one
      if (selectedNotification && selectedNotification.id === notificationId) {
        setSelectedNotification(prev => prev ? { ...prev, isRead: true } : null);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    setSelectedNotification(notification);
    // Auto-mark as read when opened
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  const dismissNotification = async (notificationId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent opening the modal
    }
    
    setDismissing(notificationId);
    
    try {
      // Mark as read first if not already
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.isRead) {
        await markAsRead(notificationId);
      }
      
      // Remove from local state (client-side dismissal)
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Show a brief success message
      console.log('Notification dismissed from view');
    } catch (error) {
      console.error('Error dismissing notification:', error);
    } finally {
      setDismissing(null);
    }
  };

  const getTypeColor = (type: string, priority: string) => {
    if (priority === 'urgent') return 'text-red-600 bg-red-50 border-red-200';
    
    switch (type) {
      case 'grounds': return 'text-green-700 bg-green-50 border-green-300';
      case 'horse': return 'text-amber-700 bg-amber-50 border-amber-300';
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'payment': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'event': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'maintenance': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
        <span className="ml-2 text-gray-600">Loading notifications...</span>
      </div>
    );
  }

  // Separate notifications by category
  const groundsNotifications = notifications.filter(n => n.type === 'grounds');
  const horseNotifications = notifications.filter(n => n.type === 'horse');
  const generalNotifications = notifications.filter(n => 
    !['grounds', 'horse'].includes(n.type)
  );

  // Helper function to sort notifications
  const sortNotifications = (notifs: Notification[]) => {
    return [...notifs].sort((a, b) => {
      // Unread first
      if (a.isRead !== b.isRead) {
        return a.isRead ? 1 : -1;
      }
      // Then by priority
      const priorityOrder: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
      const aPriority = priorityOrder[a.priority] ?? 2;
      const bPriority = priorityOrder[b.priority] ?? 2;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      // Then by date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  };

  // Render function for each section
  const renderNotificationSection = (
    title: string,
    icon: AppIconName,
    notificationList: Notification[],
    sectionKey: 'grounds' | 'horse' | 'general',
    themeColor: string
  ) => {
    if (notificationList.length === 0) return null;

    const unreadCount = notificationList.filter(n => !n.isRead).length;
    const sortedList = sortNotifications(notificationList);
    const displayList = compact ? sortedList.slice(0, 3) : sortedList;
    const hasMore = compact && sortedList.length > 3;

    return (
      <div className={`mb-4 sm:mb-6 border-2 rounded-xl overflow-hidden ${themeColor}`}>
        {/* Section Header */}
        <div 
          className={`p-3 sm:p-4 cursor-pointer hover:opacity-80 transition-opacity ${themeColor.replace('border', 'bg').replace('300', '100')}`}
          onClick={() => setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
          }))}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <AppIcon name={icon} className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0 text-gray-700" />
              <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 truncate">{title}</h3>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-semibold flex-shrink-0">
                  {unreadCount} new
                </span>
              )}
              <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">
                ({notificationList.length} total)
              </span>
            </div>
            <span className="text-xl sm:text-2xl text-gray-600 flex-shrink-0 ml-2">
              {expandedSections[sectionKey] ? '▼' : '▶'}
            </span>
          </div>
        </div>

        {/* Section Content */}
        {expandedSections[sectionKey] && (
          <div className="p-3 sm:p-4 bg-white space-y-2 sm:space-y-3">
            {displayList.map(notification => (
              <div
                key={notification.id}
                className={`p-3 sm:p-4 rounded-lg border transition-all cursor-pointer hover:shadow-md hover:scale-[1.01] relative ${
                  notification.isRead 
                    ? 'bg-white border-gray-200 hover:bg-gray-50' 
                    : `${getTypeColor(notification.type, notification.priority)} border-l-4 hover:shadow-lg`
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Dismiss Button */}
                {!compact && (
                  <button
                    onClick={(e) => dismissNotification(notification.id, e)}
                    disabled={dismissing === notification.id}
                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title="Remove from view"
                  >
                    {dismissing === notification.id ? (
                      <span className="text-xs">...</span>
                    ) : (
                      <AppIcon name="close" className="w-4 h-4" />
                    )}
                  </button>
                )}
                
                <div className="flex items-start justify-between pr-6 sm:pr-8">
                  <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                    <AppIcon name={notificationTypeIcon(notification.type)} className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1 gap-2">
                        <h4 className={`font-medium text-sm sm:text-base flex-1 min-w-0 ${notification.isRead ? 'text-gray-900' : 'text-gray-900 font-semibold'}`}>
                          {notification.title}
                        </h4>
                        <div className="flex items-center space-x-1 sm:space-x-2 text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
                          <span className="hidden sm:inline">{formatDate(notification.createdAt)}</span>
                          {!notification.isRead && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                      </div>
                      <p className={`text-xs sm:text-sm break-words ${notification.isRead ? 'text-gray-600' : 'text-gray-700'} line-clamp-2 sm:line-clamp-3`}>
                        {linkify(notification.message)}
                      </p>
                      {notification.priority === 'urgent' && (
                        <div className="mt-2 inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                          URGENT
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-1 sm:mt-2">
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span className="capitalize hidden sm:inline">
                            {notification.type} • {notification.priority} priority
                          </span>
                          <span className="sm:hidden text-xs text-gray-500">
                            {formatDate(notification.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* View More Button */}
            {hasMore && onViewAll && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewAll();
                }}
                className="w-full py-2.5 sm:py-3 text-center text-ggaa-green hover:text-ggaa-green/80 font-medium hover:bg-green-50 rounded-lg transition-colors text-xs sm:text-sm"
              >
                View {sortedList.length - 3} more {title.toLowerCase()} →
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (notifications.length === 0) {
    return (
      <div className="px-12 py-10">
        <EmptyState
          icon="emptyInbox"
          title="No notifications yet"
          description="You'll see ranch updates and announcements here"
        />
      </div>
    );
  }

  const totalUnreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className={compact ? '' : 'px-12 py-10'}>
      {!compact && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-3xl font-bold text-gray-900">Ranch Notifications</h2>
            {totalUnreadCount > 0 && (
              <div className="bg-red-500 text-white px-4 py-2 rounded-full font-semibold flex items-center space-x-2">
                <AppIcon name="bell" className="w-5 h-5" />
                <span>{totalUnreadCount} Unread</span>
              </div>
            )}
          </div>
          <p className="text-gray-600">Stay updated on important ranch news and your horses</p>
        </div>
      )}

      {/* Three distinct sections */}
      {renderNotificationSection(
        'Ranch Grounds Notifications',
        'home',
        groundsNotifications,
        'grounds',
        'border-green-300'
      )}

      {renderNotificationSection(
        'Horse & Care Notifications',
        'horse',
        horseNotifications,
        'horse',
        'border-amber-300'
      )}

      {renderNotificationSection(
        'General Announcements',
        'notifications',
        generalNotifications,
        'general',
        'border-blue-300'
      )}

      {compact && maxItems && notifications.length > maxItems && onViewAll && (
        <div className="mt-3 sm:mt-4 text-center">
          <button 
            onClick={onViewAll}
            className="text-ggaa-green hover:text-ggaa-green/80 text-xs sm:text-sm font-medium hover:underline"
          >
            View all {notifications.length} notifications →
          </button>
        </div>
      )}

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
          onMarkAsRead={markAsRead}
        />
      )}
    </div>
  );
} 